import { type ReactNode, useEffect, useState } from 'react'
import {
    Alert,
    AlertIcon,
    Button,
    Center,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    VStack,
} from '@chakra-ui/react'
import dynamic, { type DynamicOptionsLoadingProps } from 'next/dynamic'
import { TrpcErrorChannel } from 'lib/trpc'
import type { TwoFactorVerificationModalBodyProps } from './TwoFactorVerificationModalBody'

export const TwoFactorVerificationModalLoading = ({
    error,
    retry,
}: Pick<DynamicOptionsLoadingProps, 'error' | 'retry'>) =>
    error ? (
        <VStack spacing={4} align="stretch">
            <Alert status="error">
                <AlertIcon />
                We couldn&apos;t load verification. Please try again.
            </Alert>
            <Button onClick={retry}>Try again</Button>
        </VStack>
    ) : (
        <Center role="status" aria-label="Loading verification">
            <Spinner />
        </Center>
    )

const LazyTwoFactorVerificationModalBody = dynamic(
    () => import('./TwoFactorVerificationModalBody'),
    { ssr: false, loading: TwoFactorVerificationModalLoading },
)

export const TwoFactorVerificationModalLayout = ({
    isOpen,
    onClose,
    children,
}: {
    isOpen: boolean
    onClose: () => void
    children: ReactNode
}) => (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="green.transparent_10">
                Confirm it&apos;s you
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6}>{children}</ModalBody>
        </ModalContent>
    </Modal>
)

export const TwoFactorVerificationModalInner = ({
    isOpen,
    onClose,
    onSuccess,
    newPhoneNumber,
    forceOtp,
}: { isOpen: boolean } & TwoFactorVerificationModalBodyProps) => (
    <TwoFactorVerificationModalLayout isOpen={isOpen} onClose={onClose}>
        {isOpen && (
            <LazyTwoFactorVerificationModalBody
                onSuccess={onSuccess}
                onClose={onClose}
                newPhoneNumber={newPhoneNumber}
                forceOtp={forceOtp}
            />
        )}
    </TwoFactorVerificationModalLayout>
)

export const GlobalTwoFactorVerificationModal = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [openCount, setOpenCount] = useState(0)

    // VerificationChallenge events are not replayed, so keep this listener mounted.
    useEffect(() => {
        const handler = () => {
            setOpenCount(c => c + 1)
            setIsOpen(true)
        }
        const channel = new TrpcErrorChannel('VerificationChallenge')
        channel.listen(handler)
        return () => channel.unlisten(handler)
    }, [])

    const handleClose = () => {
        setIsOpen(false)
    }

    const handleSuccess = () => {
        setIsOpen(false)
        window.location.reload()
    }

    return (
        <TwoFactorVerificationModalInner
            key={openCount}
            isOpen={isOpen}
            onClose={handleClose}
            onSuccess={handleSuccess}
        />
    )
}
